<template lang="pug">
  transition(name='drawer' v-if='showUsedContentDrawer')
    .container.column(v-on-clickaway='hide')
      .header.row.middle
        simple-svg.white-icon.m-right-1(:filepath='clockIcon' width='20px' height='20px')
        h2.text-normal.white.m4-bottom-1 {{ $t('usedContent.usedContent') }}
        button.m-left-auto(@click='hide')
          simple-svg.white-icon(:filepath='crossIcon' width='20px' height='20px')
      .item-1.list.kimple-scroll
        k-select.m-right-auto.m-bottom-1(
          v-if='!loading && isAdmin && schoolsOptions.length > 1'
          :preselectedOption='schoolId || schoolsOptions[0].value'
          :placeholder='$t("registerUseModal.useForm.schoolPlaceholder")'
          name='schoolId' withBorder
          keyName='value'
          :options='schoolsOptions'
          @selectOption='changeSchool'
        )
        loader(v-if='loading' size='inner')
        .empty.column.center.m-top-2(v-else-if='!records || !records.length')
          img.il.m-bottom-2(:src='emptyImg')
          h3.title-small.dark-gray {{ $t("usedContent.emptyList") }}
        .list-container(v-else)
          .record(v-for='(record, index) in records')
            .pointer.row.middle.grade.m-bottom-1(
              v-if='differentGrade(records, record, index)'
              @click='toggleGrade(record.academicCourse.academicGrade.institutionalOrder)'
            )
              h2.text-normal.fern-green.bold
                | {{ $t(`academicGrades.${record.academicCourse.academicGrade.educationalType}.${record.academicCourse.academicGrade.translationId}`) }}
              button.m-left-auto.grade-dropdown(:class='{active: isActiveGrade(record.academicCourse.academicGrade.institutionalOrder)}')
                simple-svg.fern-green-icon(:filepath='arrowIcon' width='18px' height='18px')
            used-record-card(v-show='showRecord(record)' :record='record' @showDetail='displayDetail')
          button.primary-button.load-more(
            v-if='showLoadMore && !showDetail'
            @click='loadMore'
            :disabled='loading'
          ) {{ $t('notifications.loadMore') }}
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway'
import clockIcon from '@/assets/ic_clock_2.svg'
import crossIcon from '@/assets/ic_cross.svg'
import emptyImg from '@/assets/il_empty_gray.png'
import KSelect from '@/components/KSelect'
import { mapActions, mapGetters, mapState } from 'vuex'
import Loader from '../Loader'
import arrowLeft from '@/assets/ic_chevron-left.svg'
import arrowIcon from '@/assets/ic_chevron-down.svg'
import UsedRecordCard from './UsedRecordCard'
import userRole from '../../mixins/userRole'

export default {
  mixins: [clickaway, userRole],
  components: { Loader, UsedRecordCard, KSelect },
  data() {
    return {
      clockIcon,
      arrowIcon,
      emptyImg,
      arrowLeft,
      crossIcon,
      loading: true,
      recordSelected: null,
      schoolId: null,
      gradesAvailable: [],
      pageSize: 5,
      pageNumber: 0
    }
  },
  watch: {
    async showUsedContentDrawer() {
      if (this.showUsedContentDrawer) {
        await this.hydrateProfile()
        this.schoolId = this.isAdmin
          ? this.schoolsOptions[0].value
          : this.schoolGroup.currentSchool.id
        await this.getPage()
      } else {
        this.loading = true
        this.pageNumber = 0
        this.clearUsedContentRecords()
        this.clearUsedResourceRecords()
      }
    }
  },
  computed: {
    ...mapState({
      showUsedContentDrawer: state => state.behavior.usedContentDrawer.show,
      modalProps: state => state.behavior.usedContentDrawer.props,
      userInfo: state => state.userInfo.userInfo,
      usedContent: state => state.usedContent
    }),
    ...mapGetters({
      schoolGroup: 'getSchoolGroup'
    }),
    records() {
      return this.modalProps.contentId
        ? this.usedContent.usedContentRecords.records
        : this.usedContent.usedResourceRecords.records
    },
    totalCount() {
      return this.modalProps.contentId
        ? this.usedContent.usedContentRecords.totalCount
        : this.usedContent.usedResourceRecords.totalCount
    },
    pages() {
      return this.modalProps.contentId
        ? this.usedContent.usedContentRecords.pages
        : this.usedContent.usedResourceRecords.pages
    },
    schoolsOptions() {
      return this.schoolGroup.schools.map(school => ({ value: school.id, label: school.name }))
    },
    showLoadMore() {
      return this.pageNumber + 1 < this.pages
    }
  },
  methods: {
    ...mapActions([
      'hideElement',
      'hydrateProfile',
      'hydrateUsedContentRecords',
      'hydrateUsedResourceRecords',
      'clearUsedContentRecords',
      'clearUsedResourceRecords'
    ]),
    async getPage() {
      this.loading = true
      const { contentId, resourceId } = this.modalProps
      if (contentId) {
        await this.hydrateUsedContentRecords({
          isAdmin: this.isAdmin,
          schoolId: this.schoolId,
          pageNumber: this.pageNumber,
          pageSize: this.pageSize,
          contentId
        })
      } else if (resourceId) {
        await this.hydrateUsedResourceRecords({
          isAdmin: this.isAdmin,
          schoolId: this.schoolId,
          pageNumber: this.pageNumber,
          pageSize: this.pageSize,
          resourceId
        })
      }
      const grades = this.records.map(rec => rec.academicCourse.academicGrade.institutionalOrder)
      this.gradesAvailable = [...new Set(grades)].map(order => ({ order, active: true }))
      this.loading = false
    },
    async loadMore() {
      this.pageNumber = this.pageNumber + 1
      this.loading = true
      await this.getPage()
      this.loading = false
    },
    showRecord(record) {
      return Boolean(
        this.gradesAvailable.find(grade =>
          grade.order === record.academicCourse.academicGrade.institutionalOrder
        ).active
      )
    },
    isActiveGrade(order) {
      return Boolean(this.gradesAvailable.find(grade => grade.order === order).active)
    },
    toggleGrade(order) {
      this.gradesAvailable = this.gradesAvailable.map(grade =>
        grade.order === order
          ? ({ ...grade, active: !grade.active })
          : grade
      )
    },
    differentGrade(records, record, index) {
      const { academicGrade } = record.academicCourse
      if (index === 0) return true
      const actualGradeOrder = academicGrade.institutionalOrder
      const prevGradeOrder = records[index - 1].academicCourse.academicGrade.institutionalOrder
      return Boolean(actualGradeOrder !== prevGradeOrder)
    },
    async changeSchool(name, value) {
      this.schoolId = value
      this.clearUsedContentRecords()
      this.clearUsedResourceRecords()
      await this.getPage()
    },
    displayDetail(record) {
      this.showDetail = true
      this.recordSelected = record
    },
    hideDetail() {
      this.showDetail = false
      this.recordSelected = null
    },
    hide() {
      this.hideDetail()
      this.hideElement({ element: 'usedContentDrawer' })
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'src/scss/variables/colors';
@import 'src/scss/variables/sizes';

.container{
  position: fixed;
  z-index: 2;
  width: 430px;
  margin-top: $navbar-height;
  height: calc(100vh - #{$navbar-height});
  right: 0;
  top: 0;
  background-color: $white;
}

.load-more{
  width: 100%;
  max-width: none;
  margin-top: 10px;
  background-color: $black;
  border-color: $black;
  &:hover{
    color: $black !important;
  }
  &:active{
    background-color: $black !important;
    border-color: $black !important;
  }
  &:focus{
    border-color: $black !important;
  }
}

.header{
  background-color: $fern-green;
  padding: 0 10px;
  height: 50px;
}

.list{
  border-left: 1px solid $gray;
  padding: 10px;
  overflow-y: auto;
}

.list-container{
  &>*:not(:last-child){
    margin-bottom: 10px;
  }
}

.grade::before{
  content: "";
  left: 0;
  width: 6px;
  height: 6px;
  margin-right: 8px;
  border-radius: 50%;
  background-color: $fern-green;
}

.grade-dropdown{
  &.active{
    transform: rotate(0);
    transition: transform .5s ease;
  }
  transform: rotate(90deg);
  transition: transform .5s ease;
}

.empty{
  text-align: center;
  .il{
    width: 250px;
  }
}

.drawer-enter, .drawer-leave-to{
  transform: translate(100%);
  opacity: 0;
}

.drawer-enter-active, .drawer-leave-active{
  transition: all .3s ease;
}
</style>
